<template>
    <div>
        <v-card v-if="!isLoading" :elevation="elevation" class="text-body-2">
            <div class="text-h6 font-weight-medium pb-4">{{ $t('client_settings') }}</div>
            <v-checkbox
                v-model="calculationsAndSettings.travelersQuantityVisible"
                density="compact"
                hide-details
                data-cy="travelers-quantity-visible"
            >
                <template v-slot:label>
                    <span class="text-body-2">
                        {{ $t('visible_for_client') }}
                    </span>
                </template>
            </v-checkbox>
            <v-row dense class="align-center justify-space-between">
                <v-col cols="6" md="5" class="pl-9" align-self="center">
                    {{ `${$t('quantity_of_travelers')}:` }}
                </v-col>
                <v-col cols="4" md="2">
                    <v-text-field
                        v-model="calculationsAndSettings.travelersQuantity"
                        type="number"
                        min="0"
                        max="99"
                        density="compact"
                        hide-details
                        single-line
                        data-cy="travelers-quantity"
                        class="ma-0 pa-0 input--no-arrows"
                    />
                </v-col>
            </v-row>
            <v-checkbox
                v-model="calculationsAndSettings.focQuantityVisible"
                density="compact"
                hide-details
                data-cy="foc-quantity-visible"
            >
                <template v-slot:label>
                    <span class="text-body-2">
                        {{ $t('visible_for_client') }}
                    </span>
                </template>
            </v-checkbox>
            <v-row dense class="align-center justify-space-between">
                <v-col cols="6" md="5" class="pl-9" align-self="center">
                    {{ `${$t('quantity_free_of_charge')}:` }}
                </v-col>
                <v-col cols="4" md="2">
                    <v-text-field
                        v-model="calculationsAndSettings.focQuantity"
                        type="number"
                        min="0"
                        max="99"
                        density="compact"
                        hide-details
                        single-line
                        data-cy="foc-quantity"
                        class="ma-0 pa-0 input--no-arrows"
                    />
                </v-col>
            </v-row>
            <v-row dense class="pb-5 justify-space-between">
                <v-col cols="6" md="5" class="pl-9" align-self="center">
                    {{ `${$t('price_per_person')}:` }}
                </v-col>
                <v-col class="flex-grow-0 mr-1">
                    {{ $filters.priceFormat(calculationsAndSettings.pricePerPerson, calculationsAndSettings.currency) }}
                </v-col>
            </v-row>
            <div class="text-subtitle-1 font-weight-medium">{{ $t('show_prices_for_client') }}</div>
            <v-col cols="12" class="pb-4 pt-2 px-2">
                <v-radio-group
                    v-model="calculationsAndSettings.clientPriceType"
                    color="primary"
                    density="compact"
                    class="mt-0"
                    hide-details
                >
                    <v-radio value="FOR_ALL_SERVICES" data-cy="prices-for-all-services">
                        <template v-slot:label>
                            <span class="text-body-2">
                                {{ $t('for_all_services_within_trip') }}
                            </span>
                        </template>
                    </v-radio>
                    <v-radio value="ONLY_TOTAL" data-cy="prices-for-total-services">
                        <template v-slot:label>
                            <span class="text-body-2">
                                {{ $t('only_total') }}
                            </span>
                        </template>
                    </v-radio>
                </v-radio-group>
            </v-col>
            <div class="text-subtitle-1 font-weight-medium">{{ $t('show_cancellation_policy_for_client') }}</div>
            <v-col cols="12" class="ppb-4 pt-2 px-2">
                <v-radio-group
                    v-model="calculationsAndSettings.cancellationChargeType"
                    color="primary"
                    density="compact"
                    class="mt-0"
                    hide-details
                >
                    <v-radio value="FOR_ALL_SERVICES" data-cy="cancellation-policy-for-all-services">
                        <template v-slot:label>
                            <span class="text-body-2">
                                {{ $t('for_all_services_within_trip') }}
                            </span>
                        </template>
                    </v-radio>
                    <v-radio value="ONLY_TOTAL" data-cy="cancellation-policy-for-total-services">
                        <template v-slot:label>
                            <span class="text-body-2">
                                {{ $t('only_total') }}
                            </span>
                        </template>
                    </v-radio>
                </v-radio-group>
            </v-col>
            <v-row no-gutters class="pt-2">
                <v-col cols="12" :md="calculationsAndSettings.cancelPenalties.length ? 12 : 8">
                    <v-btn
                        color="primary"
                        variant="text"
                        class="px-0 mb-5 text-decoration-underline text-none"
                        data-cy="add-cancel-penalties"
                        @click="addCancelPenalties"
                    >
                        {{ $t('add_cancel_penalties') }}
                    </v-btn>
                    <div v-for="(item, index) in calculationsAndSettings.cancelPenalties" :key="index">
                        <v-row dense class="pb-4">
                            <v-col cols="12" md="8">
                                <order-service-daterange
                                    :start-date-label="$t('date_from')"
                                    :end-date-label="$t('date_to')"
                                    :outlined="false"
                                    :single-line="false"
                                    :no-title="true"
                                    dense
                                    :_start-date="item.dateFrom"
                                    :_end-date="item.dateTo"
                                    :data-cy="`date-${index}`"
                                    @change-start-date="item.dateFrom = $event"
                                    @change-end-date="item.dateTo = $event"
                                />
                            </v-col>
                            <v-col cols="6" md="3">
                                <v-text-field
                                    v-model="item.amount"
                                    :label="$t('calculation_amount')"
                                    type="number"
                                    min="0"
                                    density="compact"
                                    hide-details
                                    :data-cy="`amount-${index}`"
                                    class="input--no-arrows"
                                />
                            </v-col>
                            <v-col cols="1" align-self="center" class="flex-shrink-0">
                                <v-btn
                                    color="error"
                                    variant="text"
                                    icon="mdi-delete"
                                    size="small"
                                    :data-cy="`delete-${index}`"
                                    @click="deleteCancelPenalties(index)"
                                />
                            </v-col>
                        </v-row>
                    </div>
                </v-col>
                <v-divider class="mt-5 pb-2" />
            </v-row>
            <v-row no-gutters class="flex-col justify-end pa-0 gc-2">
                <v-btn
                    :loading="isSaving"
                    :disabled="isSaving"
                    color="primary"
                    variant="text"
                    data-cy="cancel"
                    @click="closeModal"
                >
                    {{ $t('cancel') }}
                </v-btn>
                <v-btn
                    :loading="isSaving"
                    :disabled="isSaving"
                    color="primary"
                    variant="flat"
                    data-cy="save"
                    @click="save"
                >
                    {{ $t('save') }}
                </v-btn>
            </v-row>
        </v-card>
        <v-card v-else :elevation="elevation">
            <v-card-text>
                {{ $t('stand_by') }}
                <v-progress-linear indeterminate color="primary" class="mb-0" />
            </v-card-text>
        </v-card>
    </div>
</template>

<script lang="ts">
    import {Component, Prop, Vue, Emit} from 'vue-facing-decorator'
    import OrderServiceDaterange from '~/components/account/filters/OrderServiceDaterange.vue'

    @Component({
        components: {OrderServiceDaterange},
    })
    export class OrderCalculationsAndSettings extends Vue {
        @Prop() orderId
        @Prop() elevation

        @Emit()
        closeModal() {}

        isLoading = false
        isSaving = false
        calculationsAndSettings = {
            travelersQuantity: 0,
            travelersQuantityVisible: false,
            focQuantity: 0,
            focQuantityVisible: false,
            clientPriceType: '',
            cancellationChargeType: '',
            cancelPenalties: [],
        }

        async mounted() {
            try {
                this.isLoading = true
                this.calculationsAndSettings = await this.$api.orderCalculator.get({orderId: this.orderId})
                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.isLoading = false
            }
        }

        async save() {
            try {
                this.isSaving = true
                const request = {
                    travelersQuantity: this.calculationsAndSettings.travelersQuantity,
                    travelersQuantityVisible: this.calculationsAndSettings.travelersQuantityVisible,
                    focQuantity: this.calculationsAndSettings.focQuantity,
                    focQuantityVisible: this.calculationsAndSettings.focQuantityVisible,
                    clientPriceType: this.calculationsAndSettings.clientPriceType,
                    cancellationChargeType: this.calculationsAndSettings.cancellationChargeType,
                    cancelPenalties: this.calculationsAndSettings.cancelPenalties,
                }

                this.calculationsAndSettings = await this.$api.orderCalculator.put(this.orderId, request)
                this.$toast.success(this.$t('success_message.saved_successfully'))
            } catch (e) {
                this.$toast.error(e.msg || e.errors[0].message)
            } finally {
                this.isSaving = false
            }
        }

        addCancelPenalties() {
            this.calculationsAndSettings.cancelPenalties.push({
                dateFrom: null,
                dateTo: null,
                amount: 0,
            })
        }

        deleteCancelPenalties(i) {
            this.calculationsAndSettings.cancelPenalties.splice(i, 1)
        }
    }

    export default OrderCalculationsAndSettings
</script>
